import React from 'react';
import { FileTextOutlined, FileAddOutlined, ExceptionOutlined, RetweetOutlined } from '@ant-design/icons';
import Icon from '@ant-design/icons';
import { JdsD86ReportIcon as JdsD86Icon } from '@/assets/icons/JdsD86ReportIcon';
import { JdsD92ReportIcon as JdsD92Icon } from '@/assets/icons/JdsD92ReportIcon';
import type { CustomReportItem } from './types';
import { I18n } from '@aws-amplify/core';

const ICON_STYLE = { fontSize: '30px' };

export const ACTION_PLAN_COLUMN_VALUES = [
	'status',
	'score',
	'responsible',
	'due_date',
	'creation_date',
	'end_date',
	'author',
	'priority',
	'investment',
	'origin',
	'tasks',
	'attachment_number',
	'evidence_number',
	'progress'
] as const;

export const ACTION_PLAN_COLUMN_LABELS = {
	status: I18n.get('Status'),
	score: I18n.get('Score '),
	responsible: I18n.get('Responsible'),
	due_date: I18n.get('Due date '),
	creation_date: I18n.get('Creation date'),
	end_date: I18n.get('Completion date'),
	author: I18n.get('Author'),
	priority: I18n.get('Priority'),
	investment: I18n.get('Investment'),
	origin: I18n.get('Source'),
	tasks: I18n.get('Tasks'),
	attachment_number: I18n.get('Number of attachments'),
	evidence_number: I18n.get('Number of evidences'),
	progress: I18n.get('Progress')
} as const;

interface ResponsiveColumnOption {
	label: string;
	value: string;
	className?: 'md-responsive' | 'lg-responsive';
}

interface ResponsiveColumnGroup {
	options: ResponsiveColumnOption[];
}

export const RESPONSIVE_COLUMN_GROUPS: ResponsiveColumnGroup[] = [
	{
		options: [
			{ label: ACTION_PLAN_COLUMN_LABELS.status, value: 'status' },
			{ label: ACTION_PLAN_COLUMN_LABELS.score, value: 'score' },
			{ label: ACTION_PLAN_COLUMN_LABELS.responsible, value: 'responsible' },
			{ label: ACTION_PLAN_COLUMN_LABELS.due_date, value: 'due_date', className: 'md-responsive' }
		]
	},
	{
		options: [
			{ label: ACTION_PLAN_COLUMN_LABELS.due_date, value: 'due_date', className: 'lg-responsive' },
			{ label: ACTION_PLAN_COLUMN_LABELS.creation_date, value: 'creation_date' },
			{ label: ACTION_PLAN_COLUMN_LABELS.end_date, value: 'end_date' },
			{ label: ACTION_PLAN_COLUMN_LABELS.author, value: 'author' },
			{ label: ACTION_PLAN_COLUMN_LABELS.priority, value: 'priority', className: 'md-responsive' },
			{ label: ACTION_PLAN_COLUMN_LABELS.investment, value: 'investment', className: 'md-responsive' }
		]
	},
	{
		options: [
			{ label: ACTION_PLAN_COLUMN_LABELS.priority, value: 'priority', className: 'lg-responsive' },
			{ label: ACTION_PLAN_COLUMN_LABELS.investment, value: 'investment', className: 'lg-responsive' },
			{ label: ACTION_PLAN_COLUMN_LABELS.origin, value: 'origin' },
			{ label: ACTION_PLAN_COLUMN_LABELS.tasks, value: 'tasks' },
			{ label: ACTION_PLAN_COLUMN_LABELS.attachment_number, value: 'attachment_number', className: 'md-responsive' },
			{ label: ACTION_PLAN_COLUMN_LABELS.evidence_number, value: 'evidence_number', className: 'md-responsive' },
			{ label: ACTION_PLAN_COLUMN_LABELS.progress, value: 'progress', className: 'md-responsive' }
		]
	},
	{
		options: [
			{ label: ACTION_PLAN_COLUMN_LABELS.attachment_number, value: 'attachment_number', className: 'lg-responsive' },
			{ label: ACTION_PLAN_COLUMN_LABELS.evidence_number, value: 'evidence_number', className: 'lg-responsive' },
			{ label: ACTION_PLAN_COLUMN_LABELS.progress, value: 'progress', className: 'lg-responsive' }
		]
	}
];

export const CUSTOM_REPORT_CONFIGS = [
	{
		name: 'BERA',
		formatted_name: 'B.E.R.A.',
		iconType: 'FileAddOutlined'
	},
	{
		name: 'SERA',
		formatted_name: 'S.E.R.A.',
		iconType: 'ExceptionOutlined'
	},
	{
		name: 'JDS-D86',
		formatted_name: 'JDS-D86',
		iconType: 'JdsD86Icon'
	},
	{
		name: 'JDS-D92',
		formatted_name: 'JDS-D92',
		iconType: 'JdsD92Icon'
	},
	{
		name: 'EWA',
		formatted_name: 'NR17',
		iconType: 'FileTextOutlined'
	},
	{
		name: 'EWA JDS-D86',
		formatted_name: 'NR17 + JDS-D86',
		iconType: 'FileTextOutlined'
	},
	{
		name: 'origin_not_specified',
		formatted_name: I18n.get('Origin not specified'),
		iconType: 'RetweetOutlined'
	}
] as const;

const createIcon = (iconType: string) => {
	const iconProps = { style: ICON_STYLE };

	switch (iconType) {
		case 'FileAddOutlined':
			return React.createElement(FileAddOutlined, iconProps);
		case 'ExceptionOutlined':
			return React.createElement(ExceptionOutlined, iconProps);
		case 'JdsD86Icon':
			return React.createElement(Icon, { ...iconProps, component: JdsD86Icon });
		case 'JdsD92Icon':
			return React.createElement(Icon, { ...iconProps, component: JdsD92Icon as React.ComponentType });
		case 'FileTextOutlined':
			return React.createElement(FileTextOutlined, iconProps);
		case 'RetweetOutlined':
			return React.createElement(RetweetOutlined, iconProps);
		default:
			return React.createElement(FileTextOutlined, iconProps);
	}
};

export const CUSTOM_REPORTS: CustomReportItem[] = CUSTOM_REPORT_CONFIGS.map((config) => ({
	report_name: config.name,
	formatted_name: config.formatted_name,
	icon: createIcon(config.iconType)
}));
